import Vue from 'vue';
import toastr from 'toastr';
import { i18n } from '@/plugins/i18n';
import { handlePictureOrientation, handlePictureDimension } from '@/utils/file';
import { createQueryString } from '@/utils/main';
import * as actionsTypes from './action-types';
import * as mutationsTypes from './mutation-types';


const FILE_UPLOAD_ERROR_CODE = {
  '-20000': 'message.FILE_UPLOAD_ERROR',
  '-20001': 'message.FILE_UPLOAD_ERROR_SIZE',
  '-20002': 'message.FILE_UPLOAD_ERROR',
  '-20003': 'message.FILE_UPLOAD_ERROR_FORMAT',
  '-20004': 'message.FILE_UPLOAD_ERROR_QUOTA',
  '-20005': 'message.FILE_UPLOAD_ERROR',
};
const FILE_DELETE_ERROR_CODE = [
  'message.FILE_DELETE_ERROR',
  'message.FILE_DELETE_ERROR',
  'message.FILE_DELETE_ERROR',
  'message.FILE_DELETE_ERROR',
  'message.FILE_DELETE_ERROR',
  'message.FILE_DELETE_ERROR_NO_ACCESS',
];

export default {
  async [actionsTypes.GET_FILE_DETAILS]({ commit, getters, state }, { ids, force }) {
    const idList = force
      ? ids
      : ids.filter(id => state.files[id] === undefined || state.files[id].status === 'failure');
    let fetchPromise;

    if (idList.length > 0) {
      idList.forEach(id => commit(mutationsTypes.SET_FILE_DETAIL, { id, status: 'pending' }));
      fetchPromise = Vue.axios({
        method: 'get',
        url: `/server/files/search.php?id=${idList.join(',')}`,
      })
        .then(({ data }) => {
          data.forEach(file => commit(mutationsTypes.SET_FILE_DETAIL, file));
          // IDs not found in response ; Failure (propably not existing anymore)
          idList.forEach((id) => {
            if (state.files[id].status === 'pending') {
              commit(mutationsTypes.SET_FILE_DETAIL, { id, status: 'failure' });
            }
          });
        })
        .catch((err) => {
          idList.forEach(id => commit(mutationsTypes.SET_FILE_DETAIL, { id, status: 'failure' }));
          throw err;
        });
    } else {
      // No file details to fetch
      fetchPromise = Promise.resolve();
    }
    return fetchPromise.then(() => Promise.resolve(getters.getFileDetails(ids)));
  },

  async [actionsTypes.SEARCH_FILE_DETAILS]({ commit, getters }, payload) {
    return Vue.axios.get(`/server/files/search.php${createQueryString(payload)}`)
      .then(({ data }) => {
        data.forEach(file => commit(mutationsTypes.SET_FILE_DETAIL, file));
        return getters.getFileDetails(data.map(f => f.id));
      });
  },

  async [actionsTypes.UPLOAD_FILE]({ commit }, payload) {
    const formData = new FormData();
    const filename = payload.name === undefined ? payload.file.name : payload.name;

    formData.append('MAX_FILE_SIZE', 20000000);
    if (payload.id) {
      formData.append('id', payload.id);
    }
    if (payload.pathname) {
      formData.append('pathname', payload.pathname);
    }
    return handlePictureOrientation(payload.file)
      .then(blob => handlePictureDimension(blob, 1500, 1500))
      .then((blob) => {
        formData.append('file', blob, filename);
        return Vue.axios.post('/server/file/upload', formData);
      })
      .then(({ data }) => {
        commit(mutationsTypes.SET_FILE_DETAIL, data);
        return data;
      })
      .catch((error) => {
        const i18nMsg = FILE_UPLOAD_ERROR_CODE[error.response.data.code] || 'message.FILE_UPLOAD_ERROR';
        toastr.error(`"${filename}"${i18n.t('message.TWO_DOTS')}${i18n.t(i18nMsg, { filename })}`);
        throw error;
      });
  },

  async [actionsTypes.DELETE_FILE]({ commit, state }, { id, onlyOrphan }) {
    const file = state.files[id];
    const filename = file ? file.name : '';

    return Vue.axios.post('/server/files/delete.php', { id, onlyOrphan })
      .then((result) => {
        if (result === 0) {
          // File was not deleted (onlyOrphan parameter)
          return 0;
        }
        commit(mutationsTypes.CLEAR_FILE_DETAIL, id);
        return id;
      })
      .catch((error) => {
        const i18nMsg = FILE_DELETE_ERROR_CODE[error.response.data] || 'message.FILE_DELETE_ERROR';
        toastr.error(`"${filename}"${i18n.t('message.TWO_DOTS')}${i18n.t(i18nMsg, { filename })}`);
        throw error;
      });
  },

  /** Fetch the raw data of a file */
  async [actionsTypes.FETCH_FILE_DATA]({ commit, state }, { fileId, force = false }) {
    return new Promise((resolve, reject) => {
      if (force || (!state.isFileDataLoading[`${fileId}`] && state.fileData[`${fileId}`] === undefined)) {
        commit(mutationsTypes.SET_FILE_DATA_LOADING, { id: fileId, isLoading: true });

        Vue.axios.get(
          `/server/file/${fileId}`,
          {
            responseType: 'blob',
          },
        )
          .then((response) => {
            commit(mutationsTypes.SET_FILE_DATA_LOADING, { id: fileId, isLoading: false });
            if (response && response.data) {
              const data = URL.createObjectURL(response.data);
              commit(mutationsTypes.SET_FILE_DATA, { id: fileId, data });
              resolve(data);
            } else {
              reject(0);
            }
          })
          .catch(() => {
            commit(mutationsTypes.SET_FILE_DATA_LOADING, { id: fileId, isLoading: false });
            reject(0);
          });
      } else {
        resolve(state.fileData[`${fileId}`]);
      }
    });
  },
};
